import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Grid2 as Grid,
  Typography
} from '@mui/material'
import {FormattedMessage, useIntl} from 'react-intl'
import Logo from '../portal/components/titlebar/icons/Logo'
import {useParams} from 'react-router-dom'
import {light, useRestDataSource} from '@enisco/mcc-react-lib'
import {LicenseEntity, LicenseInfoDefinition} from './definition'
import {useEffect, useState} from 'react'
import {CardProperty} from './CardProperty'
import {formatGoTime} from './utils'
import {LicenseProperty} from './LicenseProperty'
import {ThemeProvider} from '@mui/material/styles'

export const LicensePrintView = () => {
  const {application} = useParams()
  const intl = useIntl()
  const [datasource] = useRestDataSource(LicenseInfoDefinition)
  const [license, setLicense] = useState<LicenseEntity | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    datasource
      .read({
        filter: {
          expression: `application = '${application}'`
        }
      })
      .then((resp) => {
        setLoading(false)
        if (resp.totalCount != 1) {
          return
        }
        setLicense(resp.records[0])
      })
  }, [])

  useEffect(() => {
    if (!license) {
      return
    }
    const timeout = setTimeout(() => {
      window.print()
    }, 100)
    return () => clearTimeout(timeout)
  }, [license])

  if (loading) {
    return (
      <Backdrop open={true}>
        <CircularProgress />
      </Backdrop>
    )
  }

  if (!license) {
    return (
      <Alert severity={'error'}>
        <FormattedMessage
          id={'LicenseDialog.PrintView.NotFound'}
          defaultMessage='License <b>{application}</b> not found!'
          values={{
            application
          }}
        />
      </Alert>
    )
  }

  return (
    <ThemeProvider theme={light}>
      <Box sx={{p: 4}}>
        <Grid container flexDirection={'row'} columns={2}>
          <Grid size={1}>
            <Typography variant={'h4'} color={'textPrimary'}>
              <FormattedMessage
                id={'LicenseInfoDialog.Title'}
                defaultMessage='License Certificate'
                values={{
                  application: application
                }}
              />
            </Typography>
          </Grid>
          <Grid size={1} justifyContent={'flex-end'} display={'flex'}>
            <Logo
              height={'40px'}
              fixedIcon={'/mcc-portal-ui/FORCAM_ENISCO_2025.svg'}
            />
          </Grid>
        </Grid>
        <Box sx={{pt: 2}}>
          <Typography variant={'h6'} color={'textPrimary'}>
            <FormattedMessage
              id={'LicenseInfoDialog.Heading.LicenseDetails'}
              defaultMessage='License details'
            />
          </Typography>
          <Grid container columns={3} spacing={2}>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Application'
                    defaultMessage='Application'
                    description='Title of the application property in the card and info dialog'
                  />
                }
              >
                {license.application}
              </CardProperty>
            </Grid>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Customer'
                    defaultMessage='Customer'
                    description='Title of the customer property in the card and info dialog'
                  />
                }
              >
                {license.parsed.customer}
              </CardProperty>
            </Grid>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Expiration'
                    defaultMessage='Expiration'
                    description='Title of the expiration property in the card and info dialog'
                  />
                }
              >
                {formatGoTime(license.parsed.expiration, intl) ?? '-'}
              </CardProperty>
            </Grid>
            <Grid size={1}>
              <CardProperty
                label={
                  <FormattedMessage
                    id='LicenseApp.Property.Description'
                    defaultMessage='Description'
                    description='Title of the description property in the card and info dialog'
                  />
                }
              >
                {license.parsed.description ?? '-'}
              </CardProperty>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{pt: 4}}>
          <Typography variant={'h6'} color={'textPrimary'}>
            <FormattedMessage
              id={'LicenseInfoDialog.Heading.LicenseProperties'}
              defaultMessage='License properties'
            />
          </Typography>
          <Grid container columns={3} spacing={1}>
            {Object.entries(license.parsed.app_data).map(([key, value]) => (
              <Grid size={1}>
                <LicenseProperty
                  name={key}
                  translation={license.parsed.translations?.[key]}
                  value={value}
                  usage={license.usagedata?.[key]}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box sx={{pt: 4}}>
          <Typography variant={'caption'} color={'textSecondary'}>
            <FormattedMessage
              id={'LicenseInfoDialog.TOC'}
              defaultMessage='The general terms & conditions of Forcam Enisco GmbH apply'
            />
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
